import { Bot, Cable, Globe2, Workflow } from 'lucide-react';

interface FeaturesSectionProps {
  id?: string;
}

const services = [
  {
    icon: Cable,
    title: 'Data integration & sync',
    description:
      'Reliable API connections and two-way synchronization between CRM, field service, accounting, scheduling, forms, and databases.',
    examples: ['Bi-directional sync', 'Custom field mapping', 'Data quality checks'],
  },
  {
    icon: Workflow,
    title: 'Workflow automation',
    description:
      'Replace manual re-entry, brittle spreadsheets, and fragile handoffs with durable automations you can trust.',
    examples: ['Alerts and queues', 'Scheduled jobs', 'Operational triggers'],
  },
  {
    icon: Bot,
    title: 'AI & MCP integrations',
    description:
      'MCP servers, AI tool workflows, internal assistants, and secure system access designed around your real business process.',
    examples: ['Tool-calling agents', 'MCP servers', 'Private workflow copilots'],
  },
  {
    icon: Globe2,
    title: 'Websites & deployment',
    description: 'Small-business websites and custom apps deployed under your infrastructure or managed through ours.',
    examples: ['Marketing sites', 'Hosted apps', 'Forms and portals'],
  },
];

export default function FeaturesSection({ id }: FeaturesSectionProps) {
  return (
    <section id={id} className="w-full bg-[var(--color-canvas)] py-16 md:py-24 lg:py-28">
      <div className="container px-4 md:px-6">
        <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-14">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--color-copper)]">
              What we build
            </p>
            <h2 className="mt-3 max-w-xl text-3xl font-semibold leading-tight text-[var(--color-ink)] sm:text-5xl">
              Connected systems, built for specialized workflows
            </h2>
            <p className="mt-5 max-w-xl text-lg leading-8 text-[var(--color-muted)]">
              We reason across APIs, data models, sync logic, AI, and deployment—the details that make integrations
              reliable.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {services.map(({ icon: Icon, title, description, examples }) => (
              <article
                key={title}
                className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-md bg-[var(--color-signal)] text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-semibold text-[var(--color-ink)]">{title}</h3>
                </div>
                <p className="mt-4 leading-7 text-[var(--color-muted)]">{description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {examples.map((example) => (
                    <span
                      key={example}
                      className="rounded-full border border-[var(--color-line)] bg-white px-3 py-1 text-xs font-medium text-[var(--color-muted)]"
                    >
                      {example}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
